/**
 * Session Timeout Warning Modal
 * HIPAA-compliant auto-logoff warning
 */
import React from 'react';
import { AlertTriangle, Clock, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';

const SessionTimeoutWarning = ({ 
  remainingMinutes, 
  onExtendSession, 
  onLogout,
  show 
}) => {
  if (!show) return null;

  const formatTime = (minutes) => {
    const mins = Math.floor(minutes);
    const secs = Math.round((minutes - mins) * 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" data-testid="session-timeout-warning">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        {/* Header */}
        <div className="bg-yellow-500 p-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-white" />
          </div>
          <div className="text-white">
            <h2 className="text-lg font-bold">Session Timeout Warning</h2>
            <p className="text-yellow-100 text-sm">HIPAA Security Requirement</p>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex items-center justify-center mb-6">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 text-yellow-600 mb-2">
                <Clock className="w-5 h-5" />
                <span className="text-sm font-medium">Time Remaining</span>
              </div>
              <div className="text-4xl font-bold text-gray-800">
                {formatTime(remainingMinutes)}
              </div>
            </div>
          </div>

          <p className="text-gray-600 text-center mb-6">
            Your session will expire due to inactivity. To protect patient information (PHI), 
            you will be automatically logged out.
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-blue-700 text-center">
              Click "Continue Session" to stay logged in, or save your work before the timer expires.
            </p>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <Button
              onClick={onLogout}
              variant="outline"
              className="flex-1"
              data-testid="logout-now-btn"
            >
              Logout Now
            </Button>
            <Button
              onClick={onExtendSession}
              className="flex-1 bg-green-600 hover:bg-green-700"
              data-testid="extend-session-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Continue Session
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimeoutWarning;
